import { useEffect, useState, useRef } from "react";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField,
} from "@mui/material";
import { Check, ContentCopy, EditOutlined, FavoriteBorderOutlined, ExpandMore } from "@mui/icons-material";
import { useFilterContext } from "../contexts/FilterProvider";
import useSavedSearch from "../contexts/useSavedSearch";
import IconFilter from "../icons/IconFilter";
import { useSearchId } from "../contexts/SearchIdProvider";
import { EditSavedFiltersModal } from "./filters";
import SaveFiltersModal from "./filters/SaveFiltersModal";
import { SearchFilters } from "../models/search";
import useFilterOptions from "../contexts/useFilterOptions";
import emitSnackbar from "../emitSnackbar";
import { areFiltersEqual } from "./filters/FilterFunctions";
import { SavedSearch } from "../models/savedSearch";
import { defaultFilterState } from "../constants/defaultFilterState";
import { useAtom } from "jotai";
import { FilterResetAtom } from "../contexts/FilterResetAtom";

const SavedSearchesDropdown = (): JSX.Element => {
  const { searchId } = useSearchId();
  const { filterState, hasActiveFilters, resetFilters, applyFilters } = useFilterContext();
  const [filterReset] = useAtom(FilterResetAtom);
  const { savedSearches, getSavedSearch, isLoading, fetchSavedSearchById } = useSavedSearch();
  const [manageFiltersModalOpen, setManageFiltersModalOpen] = useState(false);
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const isApplyingSavedSearch = useRef(false);
  const { data: filterOptions } = useFilterOptions();
  const allFilterValues = filterOptions?.common_filters?.flatMap((filter) => filter.filter_values) || [];

  const ALL_DATA: SavedSearch = { ...defaultFilterState, title: "All Data", search_id: "all_data" };

  const CUSTOM_SEARCH: SavedSearch = { ...(filterState ?? defaultFilterState), title: "Custom", search_id: "custom" };

  const applySavedSearch = (searchId: string) => {
    const filters = getSavedSearch(searchId);
    if (filters) {
      applyFilters(filters, searchId);
    }
  };
  const savedFilterOptions = [ALL_DATA, ...(savedSearches ?? [])];
  const handleChange = (e: SelectChangeEvent): void => {
    const search_id = e.target.value;

    if (search_id === ALL_DATA.search_id) {
      resetFilters();
    } else {
      applySavedSearch(search_id);
    }
    // Mark that user triggered an apply, so effect doesn't override selection prematurely
    isApplyingSavedSearch.current = true;
    setSelectedSearchId(search_id);
  };

  const openDropdown = () => setOpen(true);
  const closeDropdown = () => setOpen(false);

  const handleManageFiltersModalClose = () => {
    setManageFiltersModalOpen(false);
    closeDropdown();
  };

  const handleSaveFiltersModalClose = () => {
    setSaveFilterModalOpen(false);
    closeDropdown();
  };

  // On initial mount, apply search if there's a searchId in context
  useEffect(() => {
    if (searchId) {
      applySavedSearch(searchId);
    }
  }, []);

  // Sync selectedSearchId based on filterState and saved searches
  useEffect(() => {
    if (!filterState) return;

    const matchingSavedSearch = savedSearches?.find((saved) => areFiltersEqual(saved, filterState));

    if (matchingSavedSearch) {
      setSelectedSearchId(matchingSavedSearch.search_id);
      // Filters match a saved search, reset applying flag
      isApplyingSavedSearch.current = false;
    } else if (hasActiveFilters) {
      // Only set custom if not applying a saved search
      if (!isApplyingSavedSearch.current) {
        setSelectedSearchId(CUSTOM_SEARCH.search_id);
      }
    } else {
      setSelectedSearchId(ALL_DATA.search_id);
      isApplyingSavedSearch.current = false;
    }
  }, [filterState, hasActiveFilters, savedSearches]);

  useEffect(() => {
    if (filterReset) {
      setSearchInput("");
      setSearchError(null);
    }
  }, [filterReset]);

  // Find search option to show as selected in dropdown
  let foundSearch = savedFilterOptions.find(({ search_id }) => search_id === selectedSearchId);

  if (!foundSearch) {
    if (selectedSearchId === CUSTOM_SEARCH.search_id) {
      foundSearch = CUSTOM_SEARCH;
      savedFilterOptions.push(CUSTOM_SEARCH);
    } else {
      foundSearch = ALL_DATA;
    }
  }

  const handleCopy = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);

    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  const handleSearchInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.stopPropagation();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (!searchInput.trim()) return;

      try {
        const result = await fetchSavedSearchById(searchInput.trim());
        const baseFilters = result.base_filters || {};

        // Flatten all base_filters arrays into one array
        const combinedFilterValues = Object.values(baseFilters).flat();

        // Check for missing filter values the user doesn't have access to
        const missingFilters = combinedFilterValues.filter((filterValue) => !allFilterValues.includes(filterValue));

        if (missingFilters.length > 0) {
          emitSnackbar("You do not have access to all required data sources for this saved search", "error", {
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 8000,
            preventDuplicate: true,
          });
        }

        // Filter baseFilters to only include accessible filters
        const filteredBaseFilters = Object.fromEntries(
          Object.entries(baseFilters).map(([key, values]) => [
            key,
            values.filter((val: string) => allFilterValues.includes(val)),
          ]),
        );

        // Apply the filtered filters, so FilterSectionVirtual only gets accessible selected filters
        if (result) {
          // Replace base_filters with filteredBaseFilters before applying
          // Add default values for stack_related and join_ids which are not stored in saved searches
          const filteredResult = {
            ...defaultFilterState,
            ...result,
            base_filters: filteredBaseFilters,
          };

          applyFilters(filteredResult as SearchFilters, result.search_id);

          const savedIds = savedSearches?.map((s) => s.search_id) || [];
          if (result.search_id !== ALL_DATA.search_id && !savedIds.includes(result.search_id)) {
            setSelectedSearchId(CUSTOM_SEARCH.search_id);
          } else {
            setSelectedSearchId(result.search_id);
          }

          setSearchError(null);
          setSearchInput("");
          setOpen(false);
        } else {
          setSearchError("Search ID not found");
        }
      } catch (error) {
        setSearchError("Search ID not found");
        console.error(error);
      }
    }
  };

  if (isLoading) {
    return <Skeleton height={40} variant="rounded" />;
  }

  return (
    <>
      <FormControl size="small" fullWidth>
        <Select
          labelId="app-dropdown-label"
          id="app-dropdown"
          variant="outlined"
          notched={false}
          open={open}
          onClose={closeDropdown}
          onOpen={openDropdown}
          value={foundSearch.search_id}
          onChange={handleChange}
          renderValue={(selected) => savedFilterOptions.find(({ search_id }) => search_id === selected)?.title}
          startAdornment={
            <InputAdornment position="start">
              <IconFilter />
            </InputAdornment>
          }
          inputProps={{
            sx: {
              color: "#13294B",
              fontWeight: 600,
              ...(foundSearch.search_id === CUSTOM_SEARCH.search_id ? { fontStyle: "italic", fontWeight: 500 } : {}),
            },
          }}
          IconComponent={ExpandMore}
          MenuProps={{
            marginThreshold: 0,
            sx: { "&& .Mui-selected": { backgroundColor: "transparent", color: "#13294B" } },
          }}
          style={{ paddingLeft: "4px" }}
        >
          {savedFilterOptions.map(({ title, search_id }) => (
            <MenuItem key={title} value={search_id} sx={{ justifyContent: "space-between" }}>
              <Box display="flex" alignItems="center">
                {search_id === foundSearch?.search_id ? (
                  <Check fontSize="small" sx={{ color: "#13294B", mr: "6px" }} />
                ) : (
                  <Box width={"26px"} />
                )}
                {title}
              </Box>
              {search_id !== ALL_DATA.search_id &&
                search_id !== CUSTOM_SEARCH.search_id &&
                (copiedId === search_id ? (
                  <Box sx={{ fontSize: 12, color: "#13294B", fontStyle: "italic", px: 1 }}>Copied</Box>
                ) : (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(search_id);
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                ))}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            sx={{ justifyContent: "flex-start", color: "#13294B" }}
            onMouseDown={() => setSaveFilterModalOpen(true)}
            disabled={foundSearch.search_id === ALL_DATA.search_id}
          >
            <FavoriteBorderOutlined fontSize="small" sx={{ mr: 2 }} /> Save as ...
          </MenuItem>
          <MenuItem
            sx={{ justifyContent: "flex-start", color: "#13294B", paddingBottom: 1 }}
            onMouseDown={() => setManageFiltersModalOpen(true)}
            disabled={savedSearches?.length === 0}
          >
            <EditOutlined fontSize="small" sx={{ mr: 2 }} /> Manage
          </MenuItem>
          <Box px={2} py={1}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search by ID..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value.trim() === "") {
                  setSearchError(null);
                }
              }}
              onKeyDown={handleSearchInputKeyDown}
              error={!!searchError}
              helperText={searchError}
            />
          </Box>
        </Select>
      </FormControl>

      <EditSavedFiltersModal open={manageFiltersModalOpen} handleModalClose={handleManageFiltersModalClose} />
      <SaveFiltersModal open={saveFilterModalOpen} handleModalClose={handleSaveFiltersModalClose} />
    </>
  );
};

export default SavedSearchesDropdown;
